import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, Download, Mail, XCircle } from 'react-feather';
import { Button } from '../../components/home/buttons';
import { Card } from '../../components/home/card';
import { ProgressBar } from '../../components/loader/progress-bar';
import { SpinTextLoader } from '../../components/loader/spin-loader';
import { getJobState } from '../../service/api.service';
import { JobState, type GetJobLambdaResultDto } from 'unvox-common';

export function ProcessingRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const jobId = (searchParams.get('jobId') || '').trim();
    const checkoutFromQuery = (searchParams.get('checkout') || '').trim().toLowerCase();
    const checkoutSessionIdFromQuery = (searchParams.get('session_id') || '').trim();
    const shouldVerifyCheckoutSession = checkoutFromQuery === 'success' || checkoutSessionIdFromQuery.length > 0;

    const [result, setResult] = useState<GetJobLambdaResultDto | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!jobId) return;
        let isActive = true;
        let timeoutId: number | undefined;

        const poll = async () => {
            try {
                const response = await getJobState(jobId);
                if (!isActive) return;

                if (response.error) {
                    if (response.error.code === 'no_such_job') {
                        navigate('/invalid-job', { replace: true });
                        return;
                    }
                    setError(response.error.message || t('route.processing.txtStatusFailed'));
                    return;
                }

                const next = response.data;
                if (!next) {
                    setError(t('route.processing.txtStatusFailed'));
                    return;
                }

                setResult(next);
                setError(null);

                if (next.state !== JobState.Completed && next.state !== JobState.Failed) {
                    timeoutId = window.setTimeout(poll, 15_000);
                }
            } catch (pollError) {
                if (!isActive) return;
                setError(t('route.processing.txtStatusFailed'));
            }
        };

        const start = async () => {
            if (shouldVerifyCheckoutSession) {
                if (!jobId || !checkoutSessionIdFromQuery) {
                    navigate('/invalid-job', { replace: true });
                    return;
                }

                const verification = await getJobState(jobId, { sessionId: checkoutSessionIdFromQuery });
                if (!isActive) return;

                if (verification.error) {
                    if (
                        verification.error.code === 'missing_job_id'
                        || verification.error.code === 'missing_session_id'
                        || verification.error.code === 'no_such_job'
                        || verification.error.code === 'invalid_checkout_session'
                    ) {
                        return navigate('/invalid-job', { replace: true });
                    }

                    return setError(verification.error.message || t('route.processing.txtStatusFailed'));
                }

                return navigate(`/processing?jobId=${encodeURIComponent(jobId)}`, { replace: true });
            }

            await poll();
        };

        void start();
        return () => {
            isActive = false;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [jobId, checkoutSessionIdFromQuery, shouldVerifyCheckoutSession, t, navigate]);

    if (!jobId) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtMissingTitle')}</h2>
                        <p className="text-sm text-secondary">{t('route.processing.txtMissingBody')}</p>
                    </div>
                    <Button variant="secondary" onClick={() => navigate('/')} className="w-full">
                        {t('route.processing.txtMissingAction')}
                    </Button>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#ffc1cc] to-[#ff6b81] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                        <XCircle className="h-7 w-7" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtErrorTitle')}</h2>
                        <p className="text-sm text-secondary">{error}</p>
                    </div>
                    <Button variant="secondary" onClick={() => navigate('/support')} className="w-full">
                        {t('route.processing.txtSupportAction')}
                    </Button>
                    <div className="text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    const status = result?.state;

    if (!status) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#baf3da] to-[#4cd4b0] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                        <Clock className="h-7 w-7" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtConfirmingTitle')}</h2>
                        <p className="text-sm text-secondary">
                            <SpinTextLoader text={t('route.processing.txtConfirmingBody')} />
                        </p>
                    </div>
                    <div className="text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    if (status === JobState.AwaitingUpload || status === JobState.AwaitingPreviewTask || status === JobState.AwaitingYoutubeTask) {
        return <Navigate to={`/preview/${encodeURIComponent(jobId)}`} replace />;
    }

    if (status === JobState.AwaitingStripeSession || status === JobState.AwaitingPayment) {
        return <Navigate to={`/payment/${encodeURIComponent(jobId)}`} replace />;
    }

    if (status === JobState.Failed) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#ffc1cc] to-[#ff6b81] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                        <XCircle className="h-7 w-7" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtErrorTitle')}</h2>
                        <p className="text-sm text-secondary">{result?.errorMessage || t('route.processing.txtStatusFailed')}</p>
                    </div>
                    <Button variant="secondary" onClick={() => navigate('/support')} className="w-full">
                        {t('route.processing.txtSupportAction')}
                    </Button>
                    <div className="text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    if (status === JobState.AwaitingProcessingTask) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#baf3da] to-[#4cd4b0] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                        <Mail className="h-7 w-7" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtWorkingTitle')}</h2>
                        <p className="text-sm text-secondary">{t('route.processing.txtWorkingBody')}</p>
                    </div>
                    <ProgressBar className="w-full max-w-sm" ariaLabel={t('route.processing.txtWorkingBody')} isActive initialDurationMs={160_000} initialTarget={80} />
                    <div className="text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    if (status !== JobState.Completed) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#baf3da] to-[#4cd4b0] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                        <Clock className="h-7 w-7" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('global.job.txtCheckingStatus')}</h2>
                        <p className="text-sm text-secondary">
                            <SpinTextLoader text={t('route.processing.txtPolling')} />
                        </p>
                    </div>
                    <div className="text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    const downloadUrl = result?.processedUrl || '';
    const hasDownloadUrl = Boolean(downloadUrl);
    const downloadFileName = (result?.downloadFileName || '').trim() || 'track_unvox.mp3';

    return (
        <Card>
            <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-[#baf3da] to-[#4cd4b0] text-white shadow-[0_18px_35px_-22px_rgba(70,45,130,0.55)]">
                    <CheckCircle className="h-7 w-7" />
                </div>
                <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-foreground">{t('route.processing.txtReadyTitle')}</h2>
                    <p className="text-xs text-secondary">{t('route.processing.txtDownloadNote')}</p>
                </div>
                <a href={hasDownloadUrl ? downloadUrl : undefined} download={hasDownloadUrl ? downloadFileName : undefined} aria-disabled={!hasDownloadUrl} className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-base font-semibold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:translate-y-0 bg-linear-to-r from-[#b78bff] via-[#8f7dff] to-[#4b7bff] text-white shadow-[0_18px_40px_-22px_rgba(55,32,120,0.55)] hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-22px_rgba(55,32,120,0.6)] ${!hasDownloadUrl ? 'cursor-not-allowed pointer-events-none opacity-60' : ''}`}>
                    <span className="flex items-center justify-center gap-2">
                        <Download className="h-4 w-4" />
                        {t('route.processing.txtDownloadAction')}
                    </span>
                </a>
                <div className="text-xs text-secondary">
                    {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                </div>
            </div>
        </Card>
    );
}
